import { OASElement } from '@oas-ui/core'
import { getViewport } from '../../overlay/floating/index.js'
import type { MenuItem } from '../menu/index.js'
import type { MenuItemKind } from '../menu/oas-menu.js'
// 图标查表走 @oas-ui/icons 运行时（registerIcon 自定义优先、内置集兜底，与 oas-icon 同源）；
// 不经 oas-icon 组件模块——避免把整个 <oas-icon> 拉进 navigation-menu 按需链
import { lookupIcon } from '@oas-ui/icons/runtime'
import { isRtl } from '../../shared/direction.js'
import { onMeasurable, type MeasureDisposer } from '../../shared/measure-when-visible.js'

export interface NavItem extends MenuItem {
  /** 链接地址（可选）；带 href 的叶子项渲染为 <a> */
  href?: string
  /** 链接打开方式（可选） */
  target?: string
  /** 链接卡描述（大面板形态下渲染在标题下方） */
  description?: string
  /** 当前页标记：链接渲染 aria-current="page"（顶级与面板链接均生效） */
  active?: boolean
  /** 二级级联子导航（面板内）：带 sub 的项渲染二级触发器，点击在面板内打开覆盖式二级面板 */
  sub?: NavItem[]
}

const STYLE = `
:host {
  display: inline-block;
  font-family: inherit;
  color: var(--oas-color-text-primary);
}
:host([hidden]) {
  display: none;
}
.nav {
  position: relative;
}
.bar {
  display: flex;
  align-items: center;
  gap: var(--oas-space-1);
  padding: var(--oas-space-1);
  background: var(--oas-color-bg);
  border: 1px solid var(--oas-color-border);
  border-radius: var(--oas-radius-md);
  position: relative;
}
.bar.vertical {
  flex-direction: column;
  align-items: stretch;
}
.top-item {
  appearance: none;
  border: none;
  background: transparent;
  color: var(--oas-color-text-primary);
  text-decoration: none;
  font-size: var(--oas-font-size-md);
  font-family: inherit;
  height: var(--oas-control-height-md);
  padding: 0 var(--oas-space-3);
  border-radius: var(--oas-radius-sm);
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: var(--oas-space-1);
  white-space: nowrap;
  box-sizing: border-box;
}
.top-item:hover,
.top-item.active,
.top-item[aria-expanded='true'] {
  background: var(--oas-color-bg-hover);
}
.top-item:focus-visible {
  outline: none;
  box-shadow: var(--oas-focus-ring);
}
.top-item[aria-disabled='true'] {
  cursor: not-allowed;
  opacity: 0.5;
}
.chevron {
  display: inline-flex;
  align-items: center;
  color: var(--oas-color-text-secondary);
  transition: transform 0.2s ease;
}
.chevron svg {
  display: block;
  width: 1em;
  height: 1em;
}
.top-item[aria-expanded='true'] .chevron {
  transform: rotate(180deg);
}
.bar.vertical .chevron {
  transform: rotate(-90deg);
}
.bar.vertical .top-item[aria-expanded='true'] .chevron {
  transform: rotate(90deg);
}
/* 活动触发器指示条：位置/宽度由 --ind-x/--ind-w（或纵向 --ind-y/--ind-h）驱动 */
.indicator {
  position: absolute;
  bottom: -2px;
  left: 0;
  width: var(--ind-w, 0);
  height: 2px;
  background: var(--oas-color-primary);
  border-radius: var(--oas-radius-full, 999px);
  transform: translateX(var(--ind-x, 0));
  opacity: 0;
  pointer-events: none;
  transition: transform 0.2s ease, width 0.2s ease, opacity 0.15s ease;
}
.indicator[data-state='open'] {
  opacity: 1;
}
.bar.vertical .indicator {
  top: 0;
  bottom: auto;
  inset-inline-start: auto;
  inset-inline-end: -2px;
  width: 2px;
  height: var(--ind-h, 0);
  transform: translateY(var(--ind-y, 0));
}
/* 统一视口容器：所有顶级项的面板渲染进同一容器。
   位置跟随激活触发器：--vp-x = 触发器书写起点侧相对 nav 的逻辑偏移（LTR 左缘 / RTL 右缘），
   缺省 0 = 未定位（无触发器/被「···」收纳）时贴 nav 起点；回折时走 --vp-x-end（见 flip-right）。 */
.viewport {
  position: absolute;
  top: calc(100% + var(--oas-space-1));
  inset-inline-start: var(--vp-x, 0);
  min-width: 200px;
  width: var(--vp-w, auto);
  height: var(--vp-h, auto);
  /* 窄屏宽度兜底：--vp-w 来自内容 scrollWidth，超宽时按视口裁剪（网格列 minmax(0,1fr) 自动收缩） */
  max-width: calc(100vw - 2 * var(--oas-space-2, 8px));
  background: var(--oas-color-bg);
  border: 1px solid var(--oas-color-border);
  border-radius: var(--oas-radius-md);
  box-shadow: var(--oas-shadow-md);
  z-index: calc(var(--oas-z-index-base, 0) + var(--oas-z-dropdown, 1000));
  opacity: 0;
  visibility: hidden;
  pointer-events: none;
  transition: width 0.2s ease, height 0.2s ease, opacity 0.15s ease;
}
.viewport.open {
  opacity: 1;
  visibility: visible;
  pointer-events: auto;
}
/* 视口边界碰撞翻转：浮出侧缘溢出时对齐父项另一缘回折（flip-right，逻辑 inset RTL 自动镜像）、
   下缘溢出时向上弹（flip-up）；竖排浮出侧不足回折（flip-left，逻辑 inset 自动镜像）。
   回折后右对齐激活触发器：--vp-x-end = 触发器书写终点侧相对 nav 的逻辑偏移。 */
.viewport.flip-right {
  inset-inline-start: auto;
  inset-inline-end: var(--vp-x-end, 0);
}
.viewport.flip-up {
  top: auto;
  bottom: calc(100% + var(--oas-space-1));
}
.viewport.vertical.flip-left {
  inset-inline-start: auto;
  inset-inline-end: calc(100% + var(--oas-space-1));
}
.viewport.vertical {
  top: var(--vp-y, 0);
  inset-inline-start: calc(100% + var(--oas-space-1));
}
/* 超大面板滚动：max-height 由 CSS 变量兜底，宿主可覆盖 --oas-nav-panel-max-height */
.panel {
  padding: var(--oas-space-2);
  max-height: var(--oas-nav-panel-max-height, 60vh);
  overflow: auto;
  /* 面板宽度下限 = 内容最小宽度：--vp-w 由 panel.scrollWidth 测得，而内容横向溢出内容盒时
     该值只含书写起点侧内边距（不含尾侧），面板会比内容所需窄一个 padding，末列卡片贴住面板边缘。
     用 min-content 兜底，让内容盒永远容得下内容（末列内边距与首列对称）。
     极端窄视口下（内容最小宽度 > max-width 上限）min-width 优先于 max-width，面板可超出视口。 */
  min-width: min-content;
}
.grid {
  list-style: none;
  margin: 0;
  padding: 0;
  display: grid;
  /* 列最小宽度下限 = 卡片内容最小宽度（不窄于最长单词）：minmax(0,1fr) 可把列压到内容最小宽度以下，
     列内文字横向溢出列外、越过列间距压到相邻列文字上（en 长单词尤甚；CJK 可任意断行不触发）。
     内容装不下时面板随内容加宽（列宽按各自 min-content 分配，非等宽），相邻列之间始终有可见间隔。 */
  grid-template-columns: repeat(var(--nav-columns, 2), minmax(min-content, 1fr));
  gap: var(--oas-space-1);
}
.card {
  padding: 0;
}
.card-link {
  display: flex;
  gap: var(--oas-space-2);
  align-items: flex-start;
  padding: var(--oas-space-2) var(--oas-space-3);
  border-radius: var(--oas-radius-sm);
  color: var(--oas-color-text-primary);
  text-decoration: none;
  cursor: pointer;
}
.card-link:hover {
  background: var(--oas-color-bg-hover);
}
.card-link:focus-visible {
  outline: none;
  box-shadow: var(--oas-focus-ring);
}
.card-link[aria-disabled='true'] {
  cursor: not-allowed;
  opacity: 0.5;
}
.icon {
  color: var(--oas-color-primary);
  display: inline-flex;
  flex-shrink: 0;
  margin-top: 2px;
}
.icon svg {
  display: block;
  width: 1em;
  height: 1em;
}
.card-title {
  display: block;
  font-weight: 500;
}
.card-desc {
  display: block;
  font-size: var(--oas-font-size-sm);
  color: var(--oas-color-text-secondary);
}
.section {
  padding: var(--oas-space-1);
}
.section-title {
  appearance: none;
  border: none;
  background: transparent;
  color: var(--oas-color-text-primary);
  font-family: inherit;
  font-size: var(--oas-font-size-md);
  font-weight: 500;
  padding: var(--oas-space-2) var(--oas-space-3);
  border-radius: var(--oas-radius-sm);
  cursor: pointer;
  width: 100%;
  text-align: start;
  display: flex;
  align-items: center;
  gap: var(--oas-space-1);
}
.section-title:hover {
  background: var(--oas-color-bg-hover);
}
.section-title:focus-visible {
  outline: none;
  box-shadow: var(--oas-focus-ring);
}
.section-links {
  list-style: none;
  margin: 0;
  padding: var(--oas-space-1) 0 0;
  padding-inline-start: var(--oas-space-2);
}
.section-links a {
  display: block;
  padding: var(--oas-space-2) var(--oas-space-3);
  border-radius: var(--oas-radius-sm);
  color: var(--oas-color-text-primary);
  text-decoration: none;
  cursor: pointer;
}
.section-links a:hover {
  background: var(--oas-color-bg-hover);
}
.section-links a:focus-visible {
  outline: none;
  box-shadow: var(--oas-focus-ring);
}
.section-links a[aria-disabled='true'] {
  cursor: not-allowed;
  opacity: 0.5;
}
/* 弹出层箭头（指向宿主触发器）：rotate45 描边菱形（与 menubar 统一）——
   跨面板边缘悬置（探出侧指向宿主），不内缩进面板内部；坐标系=触发器相对 nav 的 offset。 */
.nav > .arrow {
  position: absolute;
  top: calc(100% + var(--oas-space-1) - 6px);
  left: var(--arrow-x, 24px);
  width: var(--oas-navigation-menu-arrow-width, 12px);
  height: var(--oas-navigation-menu-arrow-height, 12px);
  border-radius: var(--oas-navigation-menu-arrow-radius, 0);
  background: var(--oas-color-bg);
  border-left: 1px solid var(--oas-color-border);
  border-top: 1px solid var(--oas-color-border);
  transform: rotate(45deg);
  z-index: calc(calc(var(--oas-z-index-base, 0) + var(--oas-z-dropdown, 1000)) + 1);
  opacity: 0;
  transition: opacity 0.15s ease;
}
/* 垂直形态：贴面板左边线（尖朝左指向触发器） */
.nav > .arrow.vertical {
  top: var(--arrow-y, 24px);
  left: calc(100% + var(--oas-space-1) - 6px);
  border-top: none;
  border-bottom: 1px solid var(--oas-color-border);
}
/* flip-up（面板翻到触发器上方）：箭头贴面板底边、尖朝下 */
.nav > .arrow.flip-up {
  top: auto;
  bottom: calc(100% + var(--oas-space-1) - 6px);
  border-left: none;
  border-top: none;
  border-right: 1px solid var(--oas-color-border);
  border-bottom: 1px solid var(--oas-color-border);
}
/* 垂直 flip-left（面板翻到触发器左侧）：箭头贴面板右边、尖朝右 */
.nav > .arrow.vertical.flip-left {
  left: auto;
  right: calc(100% + var(--oas-space-1) - 6px);
  border-bottom: none;
  border-left: none;
  border-right: 1px solid var(--oas-color-border);
  border-top: 1px solid var(--oas-color-border);
}
.nav.open > .arrow {
  opacity: 1;
}
/* 遮罩（可选 backdrop 属性时打开） */
.backdrop {
  position: fixed;
  inset: 0;
  background: var(--oas-color-overlay);
  opacity: 0;
  visibility: hidden;
  transition: opacity 0.2s ease;
  z-index: calc(calc(var(--oas-z-index-base, 0) + var(--oas-z-dropdown, 1000)) - 1);
}
.backdrop.open {
  opacity: 1;
  visibility: visible;
}
/* data-motion 方向位移动画：from-start = 从书写方向起点侧滑入（LTR 左 / RTL 右） */
.panel[data-motion='from-start'] {
  animation: nav-motion-start 0.25s ease;
}
.panel[data-motion='from-end'] {
  animation: nav-motion-end 0.25s ease;
}
/* RTL：起点/终点互换，位移动画镜像 */
:host([data-rtl]) .panel[data-motion='from-start'] {
  animation-name: nav-motion-start-rtl;
}
:host([data-rtl]) .panel[data-motion='from-end'] {
  animation-name: nav-motion-end-rtl;
}
@keyframes nav-motion-start {
  from {
    opacity: 0;
    transform: translateX(-8px);
  }
  to {
    opacity: 1;
    transform: none;
  }
}
@keyframes nav-motion-end {
  from {
    opacity: 0;
    transform: translateX(8px);
  }
  to {
    opacity: 1;
    transform: none;
  }
}
@keyframes nav-motion-start-rtl {
  from {
    opacity: 0;
    transform: translateX(8px);
  }
  to {
    opacity: 1;
    transform: none;
  }
}
@keyframes nav-motion-end-rtl {
  from {
    opacity: 0;
    transform: translateX(-8px);
  }
  to {
    opacity: 1;
    transform: none;
  }
}
/* ===== Sub 二级级联：面板内覆盖式二级面板（见 openSubPanel 设计决策注释） ===== */
.sub-trigger {
  appearance: none;
  border: none;
  background: transparent;
  color: var(--oas-color-text-primary);
  font-family: inherit;
  font-size: var(--oas-font-size-md);
  font-weight: 500;
  padding: var(--oas-space-2) var(--oas-space-3);
  border-radius: var(--oas-radius-sm);
  cursor: pointer;
  width: 100%;
  text-align: start;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--oas-space-1);
}
.sub-trigger:hover,
.sub-trigger[aria-expanded='true'] {
  background: var(--oas-color-bg-hover);
}
.sub-trigger:focus-visible {
  outline: none;
  box-shadow: var(--oas-focus-ring);
}
.sub-trigger .sub-chevron {
  display: inline-flex;
  align-items: center;
  color: var(--oas-color-text-secondary);
  transition: transform 0.2s ease;
}
.sub-trigger[aria-expanded='true'] .sub-chevron {
  transform: rotate(90deg);
}
.sub-trigger .sub-chevron svg {
  display: block;
  width: 1em;
  height: 1em;
}
.sub-panel {
  position: absolute;
  inset: 0;
  z-index: 2;
  padding: var(--oas-space-2);
  background: var(--oas-color-bg);
  overflow: auto;
  box-sizing: border-box;
  /* 内容宽度下限 = 内容最小宽度：--vp-w 由 sub.scrollWidth 测得，而内容横向溢出内容盒时
     该值只含书写起点侧内边距（不含尾侧），子面板会比内容所需窄一个 padding，末列贴住右缘。
     与 .panel 同因同解：用 min-content 兜底，让内容盒永远容得下内容（右内边距与左对称）。
     极端窄视口下（内容最小宽度超过 .viewport 的 max-width 上限）min-width 优先，子面板可超出视口。 */
  min-width: min-content;
}

.sub-panel[hidden] {
  display: none;
}
.sub-panel.open {
  animation: nav-sub-in 0.22s var(--oas-ease-out, ease-out);
}
@keyframes nav-sub-in {
  from {
    opacity: 0;
    transform: translateX(14px);
  }
  to {
    opacity: 1;
    transform: none;
  }
}
/* RTL：二级面板从书写方向另一侧滑入（进场位移镜像）；返回 chevron / sub-chevron 指向同步镜像 */
:host([data-rtl]) .sub-panel.open {
  animation-name: nav-sub-in-rtl;
}
@keyframes nav-sub-in-rtl {
  from {
    opacity: 0;
    transform: translateX(-14px);
  }
  to {
    opacity: 1;
    transform: none;
  }
}
:host([data-rtl]) .sub-trigger .sub-chevron svg {
  transform: scaleX(-1);
}
:host([data-rtl]) .sub-trigger[aria-expanded='true'] .sub-chevron {
  transform: rotate(-90deg);
}
:host([data-rtl]) .sub-back svg {
  transform: scaleX(-1);
}
.sub-back {
  appearance: none;
  border: none;
  background: transparent;
  color: var(--oas-color-text-secondary);
  font-family: inherit;
  font-size: var(--oas-font-size-sm);
  padding: var(--oas-space-1) var(--oas-space-2);
  border-radius: var(--oas-radius-sm);
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: var(--oas-space-1);
  margin-bottom: var(--oas-space-1);
}
.sub-back:hover {
  background: var(--oas-color-bg-hover);
  color: var(--oas-color-text-primary);
}
.sub-back:focus-visible {
  outline: none;
  box-shadow: var(--oas-focus-ring);
}
.sub-back svg {
  display: block;
  width: 1em;
  height: 1em;
}
.sub-links {
  list-style: none;
  margin: 0;
  padding: 0;
  display: grid;
  /* 列最小宽度下限 = 链接内容最小宽度（不窄于最长单词）：与 .grid 同因——minmax(0,1fr) 可把列压到
     内容最小宽度以下，列内文字横向溢出列外、越过列间距压到相邻列文字上（en 长单词尤甚；
     CJK 可任意断行不触发）。内容装不下时子面板随内容加宽（列宽按各自 min-content 分配，非等宽）。 */
  grid-template-columns: repeat(2, minmax(min-content, 1fr));
  gap: var(--oas-space-1);
}
.sub-links a {
  display: flex;
  align-items: center;
  gap: var(--oas-space-2);
  padding: var(--oas-space-2) var(--oas-space-3);
  border-radius: var(--oas-radius-sm);
  color: var(--oas-color-text-primary);
  text-decoration: none;
  cursor: pointer;
}
.sub-links a:hover {
  background: var(--oas-color-bg-hover);
}
.sub-links a:focus-visible {
  outline: none;
  box-shadow: var(--oas-focus-ring);
}
.sub-links a[aria-disabled='true'] {
  cursor: not-allowed;
  opacity: 0.5;
}
/* ===== panel-footer 营销位插槽：面板底部，有内容才显示 ===== */
.panel-footer {
  border-top: 1px solid var(--oas-color-border);
  margin-top: var(--oas-space-2);
  padding-top: var(--oas-space-2);
}
.panel-footer[hidden] {
  display: none;
}
/* ===== 粗指针触控目标 xl 档 48px（移动端基线；细指针桌面保持紧凑档不变） ===== */
@media (pointer: coarse) {
  .top-item {
    height: auto;
    min-height: var(--oas-control-height-xl);
  }
  .section-links a,
  .sub-links a {
    min-height: var(--oas-control-height-xl);
    display: flex;
    align-items: center;
  }
  .card-link,
  .sub-trigger {
    min-height: var(--oas-control-height-xl);
  }
}
/* ===== 顶级溢出收纳：窄屏装不下的尾部顶级项收进「···」弹层（参照 toolbar 收纳模式） ===== */
.top-item[data-collapsed] {
  display: none;
}
.top-more {
  appearance: none;
  border: none;
  background: transparent;
  color: var(--oas-color-text-primary);
  font-family: inherit;
  font-size: var(--oas-font-size-md);
  height: var(--oas-control-height-md);
  padding: 0 var(--oas-space-3);
  border-radius: var(--oas-radius-sm);
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  box-sizing: border-box;
}
.top-more:hover,
.top-more[aria-expanded='true'] {
  background: var(--oas-color-bg-hover);
}
.top-more:focus-visible {
  outline: none;
  box-shadow: var(--oas-focus-ring);
}
.top-more[hidden] {
  display: none;
}
.overflow-panel {
  position: absolute;
  top: calc(100% + var(--oas-space-1));
  inset-inline-end: 0;
  z-index: calc(var(--oas-z-index-base, 0) + var(--oas-z-dropdown, 1000));
  min-width: 160px;
  max-width: calc(100vw - 2 * var(--oas-space-2, 8px));
  max-height: 60vh;
  overflow-y: auto;
  padding: var(--oas-space-1);
  background: var(--oas-color-bg);
  border: 1px solid var(--oas-color-border);
  border-radius: var(--oas-radius-md);
  box-shadow: var(--oas-shadow-md);
  display: flex;
  flex-direction: column;
  gap: var(--oas-space-1);
}
/* 弹层下缘超出视口可见区域时向上翻转（判定走 getViewport，JS 切类） */
.overflow-panel.flip-up {
  top: auto;
  bottom: calc(100% + var(--oas-space-1));
}
.overflow-panel[hidden] {
  display: none;
}
.overflow-item {
  appearance: none;
  border: none;
  background: transparent;
  color: var(--oas-color-text-primary);
  font-family: inherit;
  font-size: var(--oas-font-size-md);
  padding: var(--oas-space-2) var(--oas-space-3);
  border-radius: var(--oas-radius-sm);
  cursor: pointer;
  width: 100%;
  box-sizing: border-box;
  text-align: start;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--oas-space-1);
  white-space: nowrap;
  text-decoration: none;
}
.overflow-item:hover {
  background: var(--oas-color-bg-hover);
}
.overflow-item:focus-visible {
  outline: none;
  box-shadow: var(--oas-focus-ring);
}
.overflow-item[aria-disabled='true'] {
  opacity: 0.5;
  cursor: not-allowed;
}
`

let viewportSeq = 0

export class OASNavigationMenu extends OASElement {
  static override get observedAttributes(): string[] {
    return [
      'items',
      'value',
      'delay-duration',
      'skip-delay-duration',
      'orientation',
      'columns',
      'backdrop',
      'keep-mounted',
      'arrow',
      'loop',
      // 书写方向：dir 变化触发重算 data-rtl 与翻转判定/方向键映射
      'dir',
    ]
  }

  private itemsList: NavItem[] = []
  private navEl: HTMLElement | null = null
  private barEl: HTMLElement | null = null
  private viewportEl: HTMLElement | null = null
  private panelEl: HTMLElement | null = null
  private indicatorEl: HTMLElement | null = null
  private arrowEl: HTMLElement | null = null
  private backdropEl: HTMLElement | null = null
  private subPanelEl: HTMLElement | null = null
  private panelFooterEl: HTMLElement | null = null
  private viewportId = `oas-nav-panel-${viewportSeq++}`
  private subPanelId = `oas-nav-sub-${viewportSeq++}`

  /** 子元素通道观察器：light DOM 里 oas-navigation-menu-item/group 增删或属性/文本变化 → 重渲染 */
  private childObserver: MutationObserver | null = null

  /** 内部打开项（非受控模式；受控模式以 value 属性为准） */
  private openValue: string | null = null
  /** 上一个打开项（计算 data-motion 方向） */
  private prevOpenValue: string | null = null
  /** 面板内 inline 二级子导航当前展开的 section（常开一项） */
  private openSection: string | null = null
  /** 面板内覆盖式二级面板（Sub 级联）当前打开项 value；null = 未打开 */
  private openSub: string | null = null
  /** 键盘导航当前层级焦点索引（顶级 activeIndex / 面板 panelIndex） */
  private activeIndex = 0
  private panelIndex = 0
  private keyboardMode = false
  /** 延迟开合计时器 */
  private openTimer: ReturnType<typeof setTimeout> | null = null
  private closeTimer: ReturnType<typeof setTimeout> | null = null
  /** 上次关闭时刻（skip-delay-duration 跳过打开延迟） */
  private lastCloseAt = 0

  /** 顶级溢出收纳：「···」按钮与弹层节点引用（render/水合路径共用） */
  private moreBtn: HTMLButtonElement | null = null
  private overflowPanelEl: HTMLElement | null = null
  /** 宿主尺寸监听：收纳重算 + 视口尺寸/翻转自愈（清理走 onCleanup） */
  private overflowObserver: ResizeObserver | null = null
  /** 视口尺寸自愈链路（零尺寸守卫 + 一次性 rAF；尺寸信号复用上面的宿主观察器） */
  private viewportMeasurer: MeasureDisposer | null = null
  /** 被收纳的顶级项 value 集合（键盘导航/roving 跳过、指示条不误定位） */
  private collapsedValues = new Set<string>()
  /** 「···」弹层开合 */
  private moreOpen = false

  /** 纯函数：SSR 快照与客户端渲染共用同一份模板，保证两路径结构严格一致 */
  private template(): string {
    return `
      <style>${STYLE}</style>
      <div class="nav" part="nav" role="navigation">
        <div class="bar" part="bar">
          <span class="indicator" part="indicator" data-state="closed" aria-hidden="true"></span>
          <button class="top-more" part="top-more" type="button" aria-haspopup="menu" aria-expanded="false" hidden>···</button>
        </div>
        <span class="arrow" part="arrow" aria-hidden="true"></span>
        <div class="viewport" part="viewport">
          <div class="panel" part="panel"></div>
          <div class="panel-footer" part="panel-footer" hidden><slot name="panel-footer"></slot></div>
          <div class="sub-panel" part="sub-panel" hidden></div>
        </div>
        <div class="overflow-panel" part="overflow-panel" role="menu" hidden></div>
        <div class="backdrop" part="backdrop" aria-hidden="true"></div>
      </div>
    `
  }

  /** 缓存节点引用 + 绑定事件 + 注册清理（render 与水合路径共用） */
  private bind(): void {
    this.navEl = this.shadow.querySelector('.nav')
    this.barEl = this.shadow.querySelector('.bar')
    this.viewportEl = this.shadow.querySelector('.viewport')
    this.panelEl = this.shadow.querySelector('.panel')
    this.indicatorEl = this.shadow.querySelector('.indicator')
    this.arrowEl = this.shadow.querySelector('.arrow')
    this.backdropEl = this.shadow.querySelector('.backdrop')
    this.subPanelEl = this.shadow.querySelector('.sub-panel')
    this.panelFooterEl = this.shadow.querySelector('.panel-footer')
    this.moreBtn = this.shadow.querySelector('.top-more')
    this.overflowPanelEl = this.shadow.querySelector('.overflow-panel')
    this.viewportEl?.setAttribute('id', this.viewportId)
    this.subPanelEl?.setAttribute('id', this.subPanelId)
    this.moreBtn?.setAttribute('aria-label', this.t('menu.more'))
    this.overflowPanelEl?.setAttribute('aria-label', this.t('menu.more'))
    // 「···」点击开合收纳弹层；弹层内 Esc 收起并归还焦点
    this.moreBtn?.addEventListener('click', () => {
      if (this.moreOpen) this.closeOverflow(true)
      else this.openOverflow()
    })
    this.overflowPanelEl?.addEventListener('keydown', (e) => {
      if ((e as KeyboardEvent).key === 'Escape') {
        e.stopPropagation()
        this.closeOverflow(true)
      }
    })
    // 顶级溢出收纳：容器宽度变化重算（断连清理后 update 幂等重建）
    this.ensureOverflowObserver()
    // 营销位插槽内容动态增减 → 重算容器显隐（宿主后插内容也能生效）
    const footerSlot = this.shadow.querySelector<HTMLSlotElement>('slot[name="panel-footer"]')
    footerSlot?.addEventListener('slotchange', () => this.syncPanelFooter())
    this.navEl?.addEventListener('keydown', (e) => this.handleKey(e as KeyboardEvent))
    this.navEl?.addEventListener('mouseleave', () => this.scheduleClose())
    // 指针进入面板区域不关闭（悬停区域 = bar + 面板）
    this.viewportEl?.addEventListener('mouseenter', () => {
      if (this.closeTimer) {
        clearTimeout(this.closeTimer)
        this.closeTimer = null
      }
    })
    this.viewportEl?.addEventListener('mouseleave', () => this.scheduleClose())
    this.backdropEl?.addEventListener('click', () => this.close())
    // document 级监听 + 悬停延迟定时器清理（类字段引用，重连经 onReconnect 幂等重挂）
    this.bindDocListeners()
    // 子元素通道：items 属性未显式设置时监听 light DOM 子元素变化（render 与水合路径共用）
    this.ensureChildObserver()
  }

  /**
   * document 级外点/键盘监听 + 悬停延迟定时器清理（render/hydrate 与断开重连路径共用）。
   * 类字段引用（handleDocPointer/handleDocumentKey）：addEventListener 同引用规范去重、
   * onCleanup 断开时清空、重连重注册不叠。
   */
  private bindDocListeners(): void {
    document.addEventListener('pointerdown', this.handleDocPointer)
    document.addEventListener('keydown', this.handleDocumentKey)
    this.onCleanup(() => {
      document.removeEventListener('pointerdown', this.handleDocPointer)
      document.removeEventListener('keydown', this.handleDocumentKey)
      if (this.openTimer) clearTimeout(this.openTimer)
      if (this.closeTimer) clearTimeout(this.closeTimer)
    })
  }

  /** 断开重连重绑（core onReconnect 钩子）：document 外点/键盘监听 cleanup 摘除后恢复——
      外点关闭/文档级键盘导航重连不失效（悬停延迟定时器清理一并重注册） */
  protected override onReconnect(): void {
    this.bindDocListeners()
    // childObserver 重建（cleanup 断开置 null 后 update/ensureChildObserver 的 null 守卫不会重建——
    // ensureChildObserver 重跑幂等（childObserver 为 null 时重建，在场时跳过 ✓）
    this.ensureChildObserver()
  }

  /** 建立宿主尺寸观察器（幂等；断连清理后由 update 重建，兼容重新挂载） */
  private ensureOverflowObserver(): void {
    if (typeof ResizeObserver === 'undefined' || this.overflowObserver) return
    this.overflowObserver = new ResizeObserver(() => this.handleHostResize())
    this.overflowObserver.observe(this)
    this.onCleanup(() => {
      this.overflowObserver?.disconnect()
      this.overflowObserver = null
    })
  }

  /**
   * 宿主尺寸变化（含浮层 hidden→visible 的 0→非 0）：重算顶级收纳 + 视口尺寸/翻转自愈。
   * 面板尺寸此前在不可测状态（宿主 0×0）下测不到，宿主拿到真实尺寸后须复测，否则尺寸状态留空、翻转判定用旧值。
   */
  private handleHostResize(): void {
    this.syncOverflowCollapse()
    this.syncViewportSize()
    this.syncViewportPosition()
  }

  /**
   * 建立视口尺寸自愈链路（幂等；断连清理后由下次「不可测」判定重建）。
   * 复用共享助手：`observeResize:false` 是因为本组件已自有的宿主尺寸观察器（收纳重算用）
   * 会在每次尺寸变化时调用同一 `syncViewportSize`，无需重复观察；助手补「待测 + 连接后一次性 rAF」。
   * 收敛时复位句柄，使再次进入不可测（再次隐藏后布局变化）时能重新武装。
   */
  private ensureViewportMeasurer(): void {
    if (this.viewportMeasurer) return
    let disposed = false
    const dispose = onMeasurable(this, () => this.syncViewportSize(), {
      observeResize: false,
      onSettle: () => {
        if (!disposed && this.viewportMeasurer === dispose) this.viewportMeasurer = null
      },
    })
    this.viewportMeasurer = dispose
    this.onCleanup(() => {
      disposed = true
      dispose()
      if (this.viewportMeasurer === dispose) this.viewportMeasurer = null
    })
  }

  protected override render(): void {
    this.shadow.innerHTML = this.template()
    this.bind()
    this.update()
  }

  /** 真水合：校验 SSR 快照结构（nav 存在）后直接接管，跳过 shadow 重建 */
  protected override hydrate(): boolean {
    if (!this.shadow.querySelector('.nav')) return false
    this.bind()
    return true
  }

  protected override update(): void {
    // RTL 逻辑方向化钩子：CSS :host([data-rtl]) 写镜像规则（chevron 翻转、动画反向等）
    this.toggleAttribute('data-rtl', isRtl(this))
    // 双通道：items 属性显式设置时数据驱动优先；否则解析子元素收敛到同一 items 模型渲染
    this.itemsList = this.hasAttribute('items') ? this.parseItems() : this.parseChildItems()
    this.pruneState()
    this.navEl?.setAttribute('aria-label', this.t('navigationMenu.label'))
    this.renderBar()
    const open = this.effectiveOpen()
    if (open && this.findItem(open)?.children?.length) {
      if (this.prevOpenValue !== open) this.setMotion(open)
      this.renderPanel()
    } else if (!this.hasAttr('keep-mounted')) {
      if (this.panelEl) this.panelEl.innerHTML = ''
    }
    this.syncOpen()
    this.syncIndicator()
    this.syncActive()
    this.syncRoving()
    this.syncPanelFooter()
    // 宿主尺寸监听幂等建立（断连清理后重新挂载时由 update 重建）
    this.ensureOverflowObserver()
    // 顶级溢出收纳重算（renderBar 重建了触发器，data-collapsed 需重新判定；未布局/SSR 不误判）
    this.syncOverflowCollapse()
  }

  /** panel-footer 营销位插槽容器：有内容才显示（宿主放 CTA 卡片） */
  private syncPanelFooter(): void {
    const wrap = this.panelFooterEl
    if (!wrap) return
    wrap.hidden = this.querySelectorAll('[slot="panel-footer"]').length === 0
    // 插槽显隐变化影响 viewport 高度过渡目标，重算尺寸变量
    this.syncViewportSize()
  }

  /** viewport 尺寸变量（--vp-w/--vp-h）：面板内容 + 营销位 + 覆盖式二级面板（打开时）。
   *  营销位按真实布局计（offsetHeight 含自身 padding；外距与上边分隔线一并计入）——
   *  手工拼「+4」与 .panel-footer 的 margin+padding+border 实际结构不符会裁切底部。
   *  零尺寸守卫：未布局/浮层 hidden 时 scrollWidth/scrollHeight 为 0——视为「尚不可测」，
   *  不写入尺寸状态（保留自然尺寸/上一次有效值），标记待测由宿主 ResizeObserver/一次性 rAF 复测自愈。
   *  @returns 本次是否测到有效尺寸（供自愈助手判定收敛） */
  private syncViewportSize(): boolean {
    const vp = this.viewportEl
    const p = this.panelEl
    if (!vp || !p) return false
    const footer = this.panelFooterEl
    const sub = this.subPanelEl
    // 二级面板打开时以其内容为准（覆盖主面板）；否则主面板 + 营销位
    const subOpen = !!sub && !!this.openSub && !sub.hidden
    const target = subOpen ? (sub as HTMLElement) : p
    const w = target.scrollWidth
    let h = target.scrollHeight
    if (!subOpen && footer && !footer.hidden) {
      const fh = footer.offsetHeight
      if (fh > 0) {
        const mt = parseFloat(getComputedStyle(footer).marginTop) || 0
        h += fh + mt + 1 // +1 分隔线上边框（viewport 为 content-box，height 即内容高）
      }
    }
    const wUsable = Number.isFinite(w) && w > 0
    const hUsable = Number.isFinite(h) && h > 0
    if (wUsable) vp.style.setProperty('--vp-w', `${w}px`)
    if (hUsable) vp.style.setProperty('--vp-h', `${h}px`)
    // 任一维不可测 → 保持待测（不写入 0），由共享自愈助手在拿到真实尺寸后复测补写
    const usable = wUsable && hUsable
    if (!usable) this.ensureViewportMeasurer()
    return usable
  }

  private parseItems(): NavItem[] {
    try {
      const parsed = JSON.parse(this.getAttr('items', '[]'))
      return Array.isArray(parsed)
        ? parsed.filter((i): i is NavItem => {
            if (!i || typeof i !== 'object') return false
            if (i.type === 'divider') return true
            if (i.type === 'group') return Array.isArray(i.children)
            return typeof i.value === 'string'
          })
        : []
    } catch {
      return []
    }
  }

  // ===== 子元素声明式通道 =====

  /**
   * 子元素通道解析层：把 light DOM 的 `<oas-navigation-menu-item>` / `<oas-navigation-menu-group>`
   * 收敛为内部 items 模型（单一渲染路径）。项缺 value 属性时跳过（与 JSON 通道
   * `typeof value === 'string'` 的过滤语义一致——open/select/键盘都依赖 value）。
   */
  private parseChildItems(): NavItem[] {
    return this.childItems(this)
  }

  /** 递归收集容器内的导航项载体（宿主 / 项元素 / 分组元素通用） */
  private childItems(container: Element): NavItem[] {
    const items: NavItem[] = []
    for (const child of Array.from(container.children)) {
      if (child.tagName === 'OAS-NAVIGATION-MENU-ITEM') {
        if (!child.hasAttribute('value')) continue
        items.push(this.childToItem(child))
      } else if (child.tagName === 'OAS-NAVIGATION-MENU-GROUP') {
        const group: NavItem = { type: 'group', children: this.childItems(child) }
        const label = child.getAttribute('label')
        if (label) group.label = label
        items.push(group)
      }
    }
    return items
  }

  /** 单个子元素 → 内部 item（默认插槽文本为 label，属性对齐 NavItem/MenuItem 标量字段） */
  private childToItem(el: Element): NavItem {
    const item: NavItem = {
      label: this.childLabel(el).trim(),
      value: el.getAttribute('value') ?? '',
    }
    const href = el.getAttribute('href')
    if (href) item.href = href
    const target = el.getAttribute('target')
    if (target) item.target = target
    const rel = el.getAttribute('rel')
    if (rel) item.rel = rel
    const icon = el.getAttribute('icon')
    if (icon) item.icon = icon
    const iconColor = el.getAttribute('icon-color')
    if (iconColor) item.iconColor = iconColor
    const description = el.getAttribute('description')
    if (description) item.description = description
    const kind = el.getAttribute('kind')
    if (kind) item.kind = kind as MenuItemKind
    if (el.hasAttribute('active')) item.active = true
    if (el.hasAttribute('disabled')) item.disabled = true
    if (el.hasAttribute('loading')) item.loading = true
    if (el.hasAttribute('danger')) item.danger = true
    // 嵌套：带 sub 属性的项，其直接子项解析为 sub（面板内覆盖式二级导航）；否则为 children
    const nested = this.childItems(el)
    if (nested.length > 0) {
      if (el.hasAttribute('sub')) item.sub = nested
      else item.children = nested
    }
    return item
  }

  /** 默认插槽 label 文本：排除嵌套数据载体（item/group）与命名插槽元素（如 slot="panel-footer"） */
  private childLabel(el: Element): string {
    let text = ''
    for (const node of el.childNodes) {
      if (node instanceof Element) {
        if (node.getAttribute('slot') != null) continue
        if (node.tagName === 'OAS-NAVIGATION-MENU-ITEM' || node.tagName === 'OAS-NAVIGATION-MENU-GROUP') {
          continue
        }
      }
      text += node.textContent ?? ''
    }
    return text
  }

  /** 子元素通道观察器：只监听 light DOM 子元素；自身动作不改 light DOM，无循环守卫需求 */
  private ensureChildObserver(): void {
    if (this.childObserver) return
    const observer = new MutationObserver(() => {
      this.update()
    })
    observer.observe(this, {
      childList: true,
      subtree: true,
      attributes: true,
      characterData: true,
      attributeFilter: [
        'value',
        'href',
        'target',
        'rel',
        'icon',
        'icon-color',
        'description',
        'active',
        'disabled',
        'loading',
        'kind',
        'danger',
        'sub',
        'label',
        'slot',
      ],
    })
    this.childObserver = observer
    this.onCleanup(() => {
      observer.disconnect()
      this.childObserver = null
    })
  }

  private pruneState(): void {
    const valid = new Set<string>()
    const collect = (items: MenuItem[]): void => {
      for (const i of items) {
        if (i.value != null) valid.add(i.value)
        if (i.children) collect(i.children)
      }
    }
    collect(this.itemsList)
    if (this.openValue && !valid.has(this.openValue)) this.openValue = null
    if (this.openSection && !valid.has(this.openSection)) this.openSection = null
    if (this.prevOpenValue && !valid.has(this.prevOpenValue)) this.prevOpenValue = null
    const n = this.itemsList.length
    if (this.activeIndex >= n) this.activeIndex = Math.max(0, n - 1)
  }

  /** 有效打开项：受控（value 属性存在）以属性为准，否则内部状态 */
  private effectiveOpen(): string {
    return this.hasAttr('value') ? this.getAttr('value', '') : (this.openValue ?? '')
  }

  private isVertical(): boolean {
    return this.getAttr('orientation', 'horizontal') === 'vertical'
  }

  private delayDuration(): number {
    const n = Number.parseInt(this.getAttr('delay-duration', '200'), 10)
    return Number.isFinite(n) && n >= 0 ? n : 200
  }

  private skipDelay(): number {
    const n = Number.parseInt(this.getAttr('skip-delay-duration', '300'), 10)
    return Number.isFinite(n) && n >= 0 ? n : 300
  }

  private columns(): number {
    const n = Number.parseInt(this.getAttr('columns', '2'), 10)
    return Number.isFinite(n) && n >= 1 ? n : 2
  }

  /** loop 循环导航开关：缺省 true（保持既有循环行为），仅显式 "false" 关闭 */
  private loopEnabled(): boolean {
    return this.getAttr('loop', '') !== 'false'
  }

  private findItem(value: string): NavItem | undefined {
    let found: NavItem | undefined
    const walk = (items: MenuItem[]): void => {
      for (const item of items) {
        if (item.value === value) found = item as NavItem
        else if (item.children) walk(item.children)
      }
    }
    walk(this.itemsList)
    return found
  }

  private indexOfTop(value: string): number {
    return this.itemsList.findIndex((i) => i.value === value)
  }

  // ================= 顶级触发器行 =================

  private renderBar(): void {
    const barEl = this.barEl
    if (!barEl) return
    for (const t of [...barEl.querySelectorAll<HTMLElement>('[part="top-item"]')]) t.remove()
    this.itemsList.forEach((item, idx) => {
      const hasChildren = !!item.children?.length
      const el = hasChildren || !item.href ? document.createElement('button') : document.createElement('a')
      el.className = 'top-item'
      el.setAttribute('part', 'top-item')
      if (item.value != null) el.dataset.value = item.value
      el.textContent = item.label ?? ''
      if (item.label) el.setAttribute('aria-label', item.label)
      if (hasChildren || !item.href) {
        el.setAttribute('type', 'button')
        el.setAttribute('aria-expanded', 'false')
        el.setAttribute('aria-controls', this.viewportId)
      } else {
        el.setAttribute('href', item.href)
        if (item.target) el.setAttribute('target', item.target)
        if (item.active) el.setAttribute('aria-current', 'page')
      }
      el.setAttribute('tabindex', idx === this.activeIndex ? '0' : '-1')
      if (item.disabled) el.setAttribute('aria-disabled', 'true')
      el.addEventListener('focus', () => {
        this.activeIndex = idx
        this.syncActive()
      })
      el.addEventListener('click', () => {
        this.keyboardMode = false
        if (item.disabled) return
        if (hasChildren) {
          if (this.effectiveOpen() === item.value) this.close()
          else this.open(item.value ?? '')
        } else {
          this.select(item)
        }
      })
      el.addEventListener('mouseenter', () => {
        this.keyboardMode = false
        if (item.disabled) return
        if (hasChildren) this.scheduleOpen(item.value ?? '')
      })
      el.addEventListener('mouseleave', () => {
        if (hasChildren) this.scheduleClose()
      })
      barEl.appendChild(el)
      if (hasChildren) {
        el.appendChild(this.createChevron())
      }
    })
    // 「···」收纳钮始终保持在触发器行末尾（renderBar 每次重建触发器，尾部位置需重排）
    if (this.moreBtn) barEl.appendChild(this.moreBtn)
  }

  // ================= 顶级溢出收纳（窄屏「···」弹层） =================

  /**
   * 重算顶级溢出收纳：bar 装不下时尾部顶级项打 data-collapsed 隐藏并镜像进「···」弹层。
   * 公开：宿主可在布局变化后手动触发（ResizeObserver 已自动监听本宿主）。
   * 只作用于横向形态；竖排列排无横向溢出概念，不收纳。SSR/未布局（clientWidth=0）不误判。
   */
  syncOverflowCollapse(): void {
    const bar = this.barEl
    const more = this.moreBtn
    if (!bar || !more) return
    const tops = [...bar.querySelectorAll<HTMLElement>('[part="top-item"]')]
    // 先复位再测量：collapsed 项 display:none 宽为 0，直接量会把已收纳态误判成无溢出
    for (const t of tops) t.removeAttribute('data-collapsed')
    this.collapsedValues.clear()
    const none = (): void => {
      more.hidden = true
      more.setAttribute('aria-expanded', 'false')
      this.closeOverflow()
    }
    if (this.isVertical() || tops.length < 2) {
      none()
      return
    }
    const avail = bar.clientWidth
    if (avail <= 0) {
      none()
      return
    }
    // 显示「···」量出其宽度（有溢出时它要占位，可用宽度须扣除）
    more.hidden = false
    const moreWidth = more.offsetWidth
    // 溢出判定用 scrollWidth > clientWidth（真实溢出），防 shrink-to-fit 假溢出
    const realOverflow = bar.scrollWidth > bar.clientWidth + 1
    // flex gap 不占 offsetWidth，按计算样式解析（未解析时兜底 --oas-space-1 = 4px）
    let gap = 4
    const gm = /([\d.]+)px/.exec(getComputedStyle(bar).gap || '')
    if (gm) gap = Number(gm[1])
    const calc = (availWidth: number): number => {
      let acc = 0
      for (let i = 0; i < tops.length; i++) {
        acc += tops[i]!.offsetWidth + (i > 0 ? gap : 0)
        if (acc > availWidth) return i
      }
      return -1
    }
    let firstOverflow = realOverflow ? calc(avail) : -1
    if (firstOverflow !== -1 && moreWidth > 0) {
      // 「···」自身占位后重算首个溢出项；兜底至少收最后一项腾位
      firstOverflow = calc(avail - moreWidth)
      if (firstOverflow === -1) firstOverflow = tops.length - 1
    }
    if (firstOverflow === -1) {
      none()
      return
    }
    tops.forEach((t, i) => {
      const collapsed = i >= firstOverflow
      t.toggleAttribute('data-collapsed', collapsed)
      if (collapsed && t.dataset.value != null) this.collapsedValues.add(t.dataset.value)
    })
    more.hidden = false
    more.setAttribute('aria-expanded', String(this.moreOpen))
    // roving 基准落在被收纳项时回退到首个可见项（隐藏项不可聚焦）
    const active = this.itemsList[this.activeIndex]
    if (!active || this.collapsedValues.has(active.value ?? '')) {
      const firstVisible = this.itemsList.findIndex((i) => !i.disabled && !this.collapsedValues.has(i.value ?? ''))
      if (firstVisible >= 0) this.activeIndex = firstVisible
    }
    this.syncRoving()
    this.renderOverflowPanel(tops.slice(firstOverflow))
  }

  /** 打开「···」收纳弹层：内容已在 syncOverflowCollapse 渲染，此处只做显隐 + 上翻判定 */
  private openOverflow(): void {
    const panel = this.overflowPanelEl
    if (!panel || panel.querySelectorAll('[part="overflow-item"]').length === 0) return
    this.moreOpen = true
    panel.hidden = false
    this.moreBtn?.setAttribute('aria-expanded', 'true')
    // 下缘超出视口可见区域（getViewport：visualViewport 优先）时向上翻转
    const rect = panel.getBoundingClientRect()
    panel.classList.toggle('flip-up', rect.top + rect.height > getViewport().height - 8)
  }

  /** 收起「···」收纳弹层；returnFocus 时焦点归还「···」按钮 */
  private closeOverflow(returnFocus = false): void {
    this.moreOpen = false
    if (this.overflowPanelEl) this.overflowPanelEl.hidden = true
    this.moreBtn?.setAttribute('aria-expanded', 'false')
    if (returnFocus) this.moreBtn?.focus()
  }

  /** 弹层镜像：被收顶级项 → 链接（叶子）或按钮（带 children，尾随 chevron），点击派发到原语义 */
  private renderOverflowPanel(collapsed: HTMLElement[]): void {
    const panel = this.overflowPanelEl
    if (!panel) return
    panel.innerHTML = ''
    for (const origin of collapsed) {
      const value = origin.dataset.value ?? ''
      const item = this.itemsList.find((i) => i.value === value)
      const hasChildren = origin.hasAttribute('aria-controls')
      const el =
        hasChildren || !origin.hasAttribute('href') ? document.createElement('button') : document.createElement('a')
      el.className = 'overflow-item'
      el.setAttribute('part', 'overflow-item')
      el.textContent = origin.getAttribute('aria-label') ?? origin.textContent ?? ''
      if (hasChildren) {
        el.setAttribute('type', 'button')
        el.appendChild(this.createChevron())
      } else if (origin.hasAttribute('href')) {
        el.setAttribute('href', origin.getAttribute('href') ?? '')
        if (origin.getAttribute('target')) el.setAttribute('target', origin.getAttribute('target')!)
        if (origin.getAttribute('aria-current') === 'page') el.setAttribute('aria-current', 'page')
      }
      if (origin.getAttribute('aria-disabled') === 'true') el.setAttribute('aria-disabled', 'true')
      el.addEventListener('click', () => {
        if (el.getAttribute('aria-disabled') === 'true') return
        this.closeOverflow()
        if (hasChildren) {
          // 打开其大面板（viewport 展示 children 网格；被收纳触发器不驱动指示条，见 syncIndicator）
          if (this.effectiveOpen() !== value) this.open(value)
        } else if (item) {
          this.select(item)
        }
      })
      panel.appendChild(el)
    }
  }

  private createChevron(): HTMLElement {
    const span = document.createElement('span')
    span.className = 'chevron'
    span.setAttribute('part', 'chevron')
    span.setAttribute('aria-hidden', 'true')
    const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg')
    svg.setAttribute('viewBox', '0 0 16 16')
    svg.setAttribute('width', '1em')
    svg.setAttribute('height', '1em')
    svg.setAttribute('aria-hidden', 'true')
    svg.setAttribute('focusable', 'false')
    svg.innerHTML =
      '<path d="M4 6 L8 10 L12 6" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>'
    span.appendChild(svg)
    return span
  }

  // ================= 面板渲染（大面板 + inline 二级子导航） =================

  private renderPanel(): void {
    const p = this.panelEl
    if (!p) return
    const value = this.effectiveOpen()
    const item = this.findItem(value)
    if (!item?.children?.length) {
      p.innerHTML = ''
      return
    }
    // 切换顶级触发器 → 二级覆盖面板复位（主面板重渲染）
    this.openSub = null
    if (this.subPanelEl) this.subPanelEl.hidden = true
    // 常开一项：默认展开第一个 inline 二级子导航 section（排除 Sub 二级触发器）
    const firstSection = item.children.find((c) => c.children?.length && !(c as NavItem).sub)
    if (!this.openSection && firstSection?.value) this.openSection = firstSection.value
    const grid = document.createElement('ul')
    grid.className = 'grid'
    grid.setAttribute('part', 'grid')
    grid.style.setProperty('--nav-columns', String(this.columns()))
    for (const child of item.children) {
      if (child.type === 'divider') continue
      if (child.type === 'group') {
        if (child.children) {
          for (const g of child.children) this.appendPanelCell(grid, g)
        }
        continue
      }
      this.appendPanelCell(grid, child)
    }
    p.innerHTML = ''
    p.appendChild(grid)
    this.syncViewportSize()
  }

  private appendPanelCell(grid: HTMLElement, item: MenuItem): void {
    if ((item as NavItem).sub?.length) {
      // Sub 二级级联：渲染二级触发器（优先于 inline section）
      grid.appendChild(this.buildSubTrigger(item as NavItem))
    } else if (item.children?.length) {
      grid.appendChild(this.buildSection(item as NavItem))
    } else {
      grid.appendChild(this.buildCard(item as NavItem))
    }
  }

  private buildCard(item: NavItem): HTMLElement {
    const li = document.createElement('li')
    li.className = 'card'
    li.setAttribute('part', 'item')
    if (item.value != null) li.dataset.value = item.value
    const a = document.createElement('a')
    a.className = 'card-link'
    a.setAttribute('part', 'card-link')
    a.setAttribute('href', item.href ?? '#')
    if (item.target) a.setAttribute('target', item.target)
    if (item.active) a.setAttribute('aria-current', 'page')
    if (item.disabled) a.setAttribute('aria-disabled', 'true')
    if (item.icon) {
      const ic = this.createIcon(item.icon, 'icon', item.iconColor)
      if (ic) a.appendChild(ic)
    }
    const text = document.createElement('span')
    text.className = 'card-text'
    const title = document.createElement('span')
    title.className = 'card-title'
    title.textContent = item.label ?? ''
    text.appendChild(title)
    if (item.description) {
      const desc = document.createElement('span')
      desc.className = 'card-desc'
      desc.textContent = item.description
      text.appendChild(desc)
    }
    a.appendChild(text)
    a.addEventListener('click', (e: Event) => {
      e.stopPropagation()
      if (item.disabled) {
        e.preventDefault()
        return
      }
      this.select(item)
    })
    li.appendChild(a)
    // li 空白处点击同样选中（demo-coverage 用 li DOM click 避免 <a> 跳转）
    li.addEventListener('click', () => {
      if (item.disabled) return
      this.select(item)
    })
    return li
  }

  private buildSection(item: NavItem): HTMLElement {
    const li = document.createElement('li')
    li.className = 'section'
    li.setAttribute('part', 'section')
    if (item.value != null) li.dataset.value = item.value
    const open = this.openSection === item.value
    const btn = document.createElement('button')
    btn.type = 'button'
    btn.className = 'section-title'
    btn.setAttribute('part', 'section-title')
    if (item.value != null) btn.dataset.value = item.value
    btn.setAttribute('aria-expanded', String(open))
    btn.textContent = item.label ?? ''
    const ul = document.createElement('ul')
    ul.className = 'section-links'
    ul.setAttribute('part', 'section-links')
    if (!open) ul.hidden = true
    for (const child of item.children ?? []) {
      const li2 = document.createElement('li')
      const a2 = document.createElement('a')
      a2.setAttribute('href', (child as NavItem).href ?? '#')
      if ((child as NavItem).target) a2.setAttribute('target', (child as NavItem).target!)
      if ((child as NavItem).active) a2.setAttribute('aria-current', 'page')
      if (child.disabled) a2.setAttribute('aria-disabled', 'true')
      a2.textContent = child.label ?? ''
      if (child.value != null) a2.dataset.value = child.value
      a2.addEventListener('click', (e: Event) => {
        e.stopPropagation()
        if (child.disabled) {
          e.preventDefault()
          return
        }
        this.select(child)
      })
      li2.appendChild(a2)
      ul.appendChild(li2)
    }
    btn.addEventListener('click', () => {
      this.keyboardMode = false
      if (item.value != null) this.toggleSection(item.value)
    })
    li.appendChild(btn)
    li.appendChild(ul)
    return li
  }

  /** inline 二级子导航：常开一项（点击已展开项保持展开，点击其他项切换） */
  private toggleSection(value: string): void {
    if (this.openSection === value) return
    this.openSection = value
    const p = this.panelEl
    if (!p) return
    for (const sec of p.querySelectorAll<HTMLElement>('[part="section"]')) {
      const isOpen = sec.dataset.value === value
      const title = sec.querySelector<HTMLElement>('[part="section-title"]')
      title?.setAttribute('aria-expanded', String(isOpen))
      const links = sec.querySelector<HTMLElement>('[part="section-links"]')
      if (links) links.hidden = !isOpen
    }
  }

  // ================= Sub 二级级联（面板内覆盖式二级面板） =================
  //
  // 设计决策：采用「面板内 absolute 覆盖式二级面板」，而非 viewport 级联换内容。
  // 理由：本组件 viewport 是单一面板容器（width/height 过渡 + data-motion 方向动画），
  // 多级内容交替塞进同一容器会破坏尺寸过渡连续性、并让主面板上下文丢失；
  // 覆盖式保留主面板 DOM 于底层，二级面板 absolute inset:0 覆盖其上（slide-in 动画），
  // Esc/ArrowLeft 逐层回退零成本。与现有 inline section 折叠并存（sub 字段优先于 children）。
  // 限制：二级面板内不再支持三级级联（sub 的 sub 不渲染）；焦点链见 handleSubKey。

  /** 二级触发器（按钮）：面板格内渲染，点击开/关覆盖式二级面板 */
  private buildSubTrigger(item: NavItem): HTMLElement {
    const li = document.createElement('li')
    li.className = 'section'
    li.setAttribute('part', 'section')
    if (item.value != null) li.dataset.value = item.value
    const btn = document.createElement('button')
    btn.type = 'button'
    btn.className = 'sub-trigger'
    btn.setAttribute('part', 'sub-trigger')
    if (item.value != null) btn.dataset.value = item.value
    btn.setAttribute('aria-expanded', 'false')
    btn.setAttribute('aria-controls', this.subPanelId)
    btn.setAttribute('aria-haspopup', 'menu')
    const label = document.createElement('span')
    label.textContent = item.label ?? ''
    btn.appendChild(label)
    const chevron = document.createElement('span')
    chevron.className = 'sub-chevron'
    chevron.setAttribute('aria-hidden', 'true')
    const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg')
    svg.setAttribute('viewBox', '0 0 16 16')
    svg.setAttribute('width', '1em')
    svg.setAttribute('height', '1em')
    svg.setAttribute('aria-hidden', 'true')
    svg.setAttribute('focusable', 'false')
    svg.innerHTML =
      '<path d="M6 4 L10 8 L6 12" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>'
    chevron.appendChild(svg)
    btn.appendChild(chevron)
    btn.addEventListener('click', () => {
      this.keyboardMode = false
      if (this.openSub === item.value) this.closeSubPanel()
      else this.openSubPanel(item.value ?? '')
    })
    li.appendChild(btn)
    return li
  }

  /** 渲染覆盖式二级面板内容：返回按钮 + sub 子项链接（href 叶子渲染 <a>） */
  private renderSubPanel(): void {
    const sp = this.subPanelEl
    if (!sp) return
    const item = this.openSub ? this.findItem(this.openSub) : undefined
    const sub = item?.sub ?? []
    sp.innerHTML = ''
    const back = document.createElement('button')
    back.type = 'button'
    back.className = 'sub-back'
    back.setAttribute('part', 'sub-back')
    back.setAttribute('aria-label', this.t('navigationMenu.back'))
    const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg')
    svg.setAttribute('viewBox', '0 0 16 16')
    svg.setAttribute('width', '1em')
    svg.setAttribute('height', '1em')
    svg.setAttribute('aria-hidden', 'true')
    svg.setAttribute('focusable', 'false')
    svg.innerHTML =
      '<path d="M10 4 L6 8 L10 12" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>'
    back.appendChild(svg)
    back.append(this.t('navigationMenu.back'))
    back.addEventListener('click', () => this.closeSubPanel())
    sp.appendChild(back)
    const ul = document.createElement('ul')
    ul.className = 'sub-links'
    ul.setAttribute('role', 'list')
    for (const s of sub) {
      const li = document.createElement('li')
      const a = document.createElement('a')
      a.className = 'sub-link'
      a.setAttribute('part', 'sub-link')
      a.setAttribute('href', s.href ?? '#')
      if (s.target) a.setAttribute('target', s.target)
      if (s.active) a.setAttribute('aria-current', 'page')
      if (s.disabled) a.setAttribute('aria-disabled', 'true')
      if (s.value != null) a.dataset.value = s.value
      a.textContent = s.label ?? ''
      a.addEventListener('click', (e: Event) => {
        e.stopPropagation()
        if (s.disabled) {
          e.preventDefault()
          return
        }
        this.select(s)
      })
      li.appendChild(a)
      ul.appendChild(li)
    }
    sp.appendChild(ul)
  }

  /** 打开二级面板：渲染内容 + 显隐 + 焦点移入首项（键盘/鼠标共用） */
  private openSubPanel(value: string): void {
    this.openSub = value
    this.renderSubPanel()
    this.syncSubOpen()
    this.keyboardMode = true
    this.panelIndex = this.firstEnabledSubIndex()
    this.focusSubPanel()
    this.syncViewportSize()
  }

  /** 关闭二级面板：焦点回触发器（Esc/ArrowLeft/返回按钮/再点触发器） */
  private closeSubPanel(): void {
    const value = this.openSub
    this.openSub = null
    this.syncSubOpen()
    this.syncViewportSize()
    if (value) {
      const trig = this.shadow.querySelector<HTMLElement>(`[part="sub-trigger"][data-value="${value}"]`)
      ;(trig as HTMLElement | null)?.focus()
    }
  }

  /** 二级面板开合同步：显隐 + 触发器 aria-expanded + 尺寸过渡 */
  private syncSubOpen(): void {
    const sp = this.subPanelEl
    if (sp) sp.hidden = !this.openSub
    for (const t of this.shadow.querySelectorAll<HTMLElement>('[part="sub-trigger"]')) {
      t.setAttribute('aria-expanded', String(this.openSub === (t.dataset.value ?? '')))
    }
  }

  private subFocusables(): HTMLElement[] {
    const sp = this.subPanelEl
    if (!sp) return []
    return [...sp.querySelectorAll<HTMLElement>('[part="sub-link"]')]
  }

  private firstEnabledSubIndex(): number {
    const list = this.subFocusables()
    return list.findIndex((el) => el.getAttribute('aria-disabled') !== 'true')
  }

  private focusSubPanel(): void {
    const list = this.subFocusables()
    const el = list[this.panelIndex]
    if (el) el.focus()
  }

  /** 图标名（查表：registerIcon 自定义优先，其次内置注册表）→ 内联 SVG。
   *  iconColor 显式时固定该色（优先于选中/禁用态默认色）；缺省 currentColor 随态着色。
   *  内置单色 path 自带 stroke="currentColor"——iconColor 显式时把 path 的 currentColor 也替换，
   *  否则 path 元素级属性压过 svg 外层 stroke（与 sidebar iconSvg 对齐） */
  private createIcon(icon: string, className = 'icon', iconColor?: string): HTMLElement | null {
    const content = lookupIcon(icon)
    if (!content) return null
    const stroke = iconColor || 'currentColor'
    const coloredContent = iconColor ? content.replace(/stroke="currentColor"/g, `stroke="${stroke}"`) : content
    const span = document.createElement('span')
    span.className = className
    span.setAttribute('aria-hidden', 'true')
    const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg')
    svg.setAttribute('viewBox', '0 0 16 16')
    svg.setAttribute('width', '1em')
    svg.setAttribute('height', '1em')
    svg.setAttribute('aria-hidden', 'true')
    svg.setAttribute('focusable', 'false')
    svg.setAttribute('fill', 'none')
    svg.setAttribute('stroke', stroke)
    svg.setAttribute('stroke-width', '1.5')
    svg.setAttribute('stroke-linecap', 'round')
    svg.setAttribute('stroke-linejoin', 'round')
    svg.innerHTML = coloredContent
    span.appendChild(svg)
    return span
  }

  // ================= 打开/关闭（受控 + 延迟 + 事件） =================

  /** 立即打开（点击/键盘路径，不走延迟） */
  private open(value: string): void {
    if (this.openTimer) {
      clearTimeout(this.openTimer)
      this.openTimer = null
    }
    if (this.closeTimer) {
      clearTimeout(this.closeTimer)
      this.closeTimer = null
    }
    // 打开大面板时同步收起「···」收纳弹层（互斥浮层，避免双重悬挂）
    this.closeOverflow()
    this.setOpenValue(value)
  }

  /** 立即关闭（点击/外部点击/Esc 路径） */
  private close(): void {
    if (this.openTimer) {
      clearTimeout(this.openTimer)
      this.openTimer = null
    }
    if (this.closeTimer) {
      clearTimeout(this.closeTimer)
      this.closeTimer = null
    }
    // 大面板关闭时同步收起「···」收纳弹层（两者互斥，避免浮层悬挂）
    this.closeOverflow()
    this.setOpenValue('')
  }

  private setOpenValue(value: string): void {
    const prev = this.effectiveOpen()
    if (prev === value) return
    if (prev && !value) this.lastCloseAt = Date.now()
    // 切换/关闭顶级触发器 → 面板级状态（section 折叠 + Sub 二级面板）复位
    this.openSection = null
    this.openSub = null
    if (this.subPanelEl) this.subPanelEl.hidden = true
    if (!this.hasAttr('value')) this.openValue = value || null
    this.emit('change', { value })
    const open = this.effectiveOpen()
    if (open && this.findItem(open)?.children?.length) {
      if (this.prevOpenValue !== open) this.setMotion(open)
      this.renderPanel()
    } else if (!this.hasAttr('keep-mounted')) {
      if (this.panelEl) this.panelEl.innerHTML = ''
    }
    this.syncOpen()
    this.syncIndicator()
    this.syncActive()
    this.syncRoving()
  }

  private setMotion(value: string): void {
    const prevIdx = this.prevOpenValue ? this.indexOfTop(this.prevOpenValue) : -1
    const idx = this.indexOfTop(value)
    // 首次打开（无上一项）或向左 → from-start；向右 → from-end
    const motion = prevIdx < 0 ? 'from-start' : idx < prevIdx ? 'from-start' : 'from-end'
    this.panelEl?.setAttribute('data-motion', motion)
    this.prevOpenValue = value
  }

  /** hover 打开：延迟 delay-duration；命中 skip-delay 时立即打开 */
  private scheduleOpen(value: string): void {
    if (this.closeTimer) {
      clearTimeout(this.closeTimer)
      this.closeTimer = null
    }
    if (this.effectiveOpen() === value) return
    if (this.openTimer) {
      clearTimeout(this.openTimer)
      this.openTimer = null
    }
    const skip = this.skipDelay() > 0 && this.lastCloseAt > 0 && Date.now() - this.lastCloseAt < this.skipDelay()
    const delay = skip ? 0 : this.delayDuration()
    if (delay <= 0) {
      this.open(value)
      return
    }
    this.openTimer = setTimeout(() => {
      this.openTimer = null
      this.open(value)
    }, delay)
  }

  /** hover 关闭：独立关闭宽限 ≥150ms（最低保底）
   *  坑记录：曾复用 delay-duration 作关闭延迟——delay-duration="0" 时关闭宽限也为 0，
   *  指针离开触发器后 setTimeout(0) 先于 viewport mouseenter 执行，面板在指针到达前
   *  就被关掉（移入子菜单即收回、无法点选）。打开延迟与关闭宽限语义解耦：delay-duration
   *  只管打开；关闭宽限取 max(delay-duration, 150)，默认 200 不变、0 延迟 demo 恢复可用 */
  private scheduleClose(): void {
    if (!this.effectiveOpen()) return
    if (this.openTimer) {
      clearTimeout(this.openTimer)
      this.openTimer = null
    }
    if (this.closeTimer) {
      clearTimeout(this.closeTimer)
      this.closeTimer = null
    }
    this.closeTimer = setTimeout(
      () => {
        this.closeTimer = null
        this.close()
      },
      Math.max(this.delayDuration(), 150),
    )
  }

  private select(item: MenuItem): void {
    this.emit('select', { value: item.value })
    this.close()
    if (this.keyboardMode) {
      const parentIdx = item.value != null ? this.indexOfTop(item.value) : -1
      if (parentIdx >= 0) this.activeIndex = parentIdx
      this.syncActive()
      this.syncRoving()
      this.focusCurrent()
    }
  }

  // ================= 外部点击关闭 =================

  private handleDocPointer = (e: PointerEvent): void => {
    if (!this.effectiveOpen() && !this.moreOpen) return
    const t = e.target as Node | null
    if (t && (t === this || this.contains(t))) return
    this.keyboardMode = false
    if (this.effectiveOpen()) this.close()
    if (this.moreOpen) this.closeOverflow()
  }

  // ================= 状态同步 =================

  private syncOpen(): void {
    if (!this.shadow) return
    const open = this.effectiveOpen()
    this.navEl?.classList.toggle('open', !!open)
    this.viewportEl?.classList.toggle('open', !!open)
    this.viewportEl?.setAttribute('data-value', open)
    this.viewportEl?.classList.toggle('vertical', this.isVertical())
    this.barEl?.setAttribute('data-orientation', this.isVertical() ? 'vertical' : 'horizontal')
    this.barEl?.classList.toggle('vertical', this.isVertical())
    for (const btn of this.shadow.querySelectorAll<HTMLElement>('[part="top-item"]')) {
      const v = btn.dataset.value ?? ''
      if (btn.hasAttribute('aria-expanded')) {
        btn.setAttribute('aria-expanded', String(!!open && open === v))
      }
    }
    if (this.indicatorEl) {
      this.indicatorEl.setAttribute('data-state', open ? 'open' : 'closed')
    }
    if (this.arrowEl) {
      this.arrowEl.hidden = this.getAttr('arrow', 'true') === 'false'
      this.arrowEl.classList.toggle('vertical', this.isVertical())
    }
    if (this.backdropEl) {
      this.backdropEl.classList.toggle('open', !!open && this.hasAttr('backdrop'))
    }
    // 碰撞翻转（右缘/下缘溢出）在每次开合/切换时重算
    this.syncViewportPosition()
  }

  /**
   * 面板位置跟随激活触发器 + 碰撞/翻转检测。
   *
   * 位置：先把激活触发器相对 nav 的逻辑偏移写入 `--vp-x`（缺省贴触发器书写起点侧）/
   * `--vp-x-end`（回折后贴触发器书写终点侧）/ `--vp-y`（竖排贴触发器顶边）；触发器缺失或
   * 被收纳（display:none 量不到 offset）时偏移归 0，回退旧的「贴 nav 起点」行为。
   *
   * 翻转：缺省侧溢出时回折（flip-right，逻辑 inset RTL 自动镜像），下缘溢出向上弹（flip-up），
   * 竖排浮出侧不足回折（flip-left）。RTL 下溢出检测镜像到另一侧（左缘 ↔ 右缘）。
   * 水平边界只取「视口缘（含 margin 安全边距）」——导航栏是 shrink-to-fit（inline-block）时栏宽
   * 只够装触发器，把栏缘当硬边界会让每个顶级项的面板都被判回折（全部右对齐）；导航栏也不是裁切
   * 容器（overflow 可见），面板本就允许越出栏外。只有「跟随后的面板」会越出视口时才回折。
   * 尺寸用 offsetWidth/scrollWidth（transform 免疫），位置用 getBoundingClientRect（面板/栏无 scale 动画）。
   */
  private syncViewportPosition(): void {
    const vp = this.viewportEl
    const bar = this.barEl
    if (!vp || !bar) return
    const open = this.effectiveOpen()
    if (!open) {
      vp.classList.remove('flip-right', 'flip-up', 'flip-left')
      return
    }
    const margin = 8
    const vw = window.innerWidth
    const vh = window.innerHeight
    const size = this.viewportContentSize()
    const rtl = isRtl(this)
    const geo = this.writeViewportOffset()
    const barRect = bar.getBoundingClientRect()
    if (this.isVertical()) {
      // 竖排浮出侧：LTR 向右（检右缘）、RTL 向左（检左缘，面板右对齐 bar 左缘外）
      const overflows = rtl ? barRect.left - size.w < margin : barRect.left + bar.offsetWidth + size.w > vw - margin
      vp.classList.toggle('flip-left', overflows)
      vp.classList.remove('flip-right')
    } else {
      const navRect = this.navEl?.getBoundingClientRect()
      // navRect.left 未布局（测试替身只给部分字段）时回退 bar 左缘
      const navLeft = navRect && Number.isFinite(navRect.left) ? navRect.left : barRect.left
      if (rtl) {
        // RTL：面板缺省右对齐触发器书写起点侧（右缘）向左展开，碰撞发生在视口左缘。
        // 左边界只取视口左缘（0）——导航栏缘不是裁切边界（见方法注释），拿它当边界会把
        // 栏内每一项都误判成回折（与 LTR 同一根因，方向镜像）。
        const leftEdge = navLeft + geo.localRight - size.w
        // 越出视口左缘即回折（回折后 flip-right 类在 RTL 下镜像为左对齐、贴触发器书写终点侧）。
        // 回折后仍越出视口右缘时同样回折：面板整体宽于视口时贴触发器一侧比另一侧更合理，
        // 且 .viewport 的 max-width 兜底会把实际宽度裁回视口内。
        vp.classList.toggle('flip-right', leftEdge < margin)
        vp.classList.remove('flip-left')
      } else {
        // 缺省面板左缘贴触发器左缘；回折后右缘贴触发器右缘
        const unflippedLeft = navLeft + geo.localLeft
        const rightEdge = unflippedLeft + size.w
        // 右边界只取视口右缘——导航栏盒宽不是硬边界（shrink-to-fit 栏宽只够装触发器，
        // 面板 min-width 200 几乎必然越过栏缘，按栏缘判定会让所有面板都右对齐）。
        vp.classList.toggle('flip-right', rightEdge > vw - margin)
        vp.classList.remove('flip-left')
      }
    }
    const vpRect = vp.getBoundingClientRect()
    const bottom = vpRect.top + size.h
    vp.classList.toggle('flip-up', bottom > vh - margin)
    // 箭头镜像 flip 状态：箭头挂 nav 下（viewport 外），面板翻转后箭头须换边贴合面板、
    // 尖端反向指向触发器——不镜像的话箭头悬空在翻转前的位置、背对触发器
    if (this.arrowEl) {
      this.arrowEl.classList.toggle('flip-up', vp.classList.contains('flip-up'))
      this.arrowEl.classList.toggle('flip-left', vp.classList.contains('flip-left'))
    }
  }

  /**
   * 把激活触发器相对 nav 的逻辑偏移写入视口定位变量：
   * - `--vp-x`：书写起点侧（LTR 左 / RTL 右）偏移——缺省面板起点对齐触发器起点；
   * - `--vp-x-end`：书写终点侧（LTR 右 / RTL 左）偏移——回折后面板终点对齐触发器终点；
   * - `--vp-y`：竖排顶边偏移（面板顶对齐激活项顶）。
   * 触发器缺失/被收纳（display:none 量不到 offset）时全部归 0——回退「贴 nav 起点」。
   * @returns nav 本地坐标系下触发器的物理左右缘（供碰撞翻转判定复用）
   */
  private writeViewportOffset(): { localLeft: number; localRight: number } {
    const vp = this.viewportEl
    const bar = this.barEl
    const nav = this.navEl
    if (!vp || !bar || !nav) return { localLeft: 0, localRight: 0 }
    const value = this.effectiveOpen()
    const trigger = value ? this.shadow.querySelector<HTMLElement>(`[part="top-item"][data-value="${value}"]`) : null
    const usable = !!trigger && !trigger.hasAttribute('data-collapsed')
    const localLeft = usable ? (bar.offsetLeft || 0) + (trigger.offsetLeft || 0) : 0
    const localRight = usable ? localLeft + (trigger.offsetWidth || 0) : 0
    const localTop = usable ? (bar.offsetTop || 0) + (trigger.offsetTop || 0) : 0
    const navW = nav.offsetWidth || bar.offsetWidth || 0
    // 逻辑书写方向：RTL 下书写起点侧在物理右缘、终点侧在物理左缘
    const start = isRtl(this) ? Math.max(0, navW - localRight) : localLeft
    const end = isRtl(this) ? localLeft : Math.max(0, navW - localRight)
    vp.style.setProperty('--vp-x', `${start}px`)
    vp.style.setProperty('--vp-x-end', `${end}px`)
    vp.style.setProperty('--vp-y', `${localTop}px`)
    return { localLeft, localRight }
  }

  /** 面板内容尺寸（主面板 + 营销位；二级面板打开时以二级内容为准）——翻转/尺寸过渡共用。
   *  营销位按真实布局计（offsetHeight 含自身 padding；外距与上边分隔线一并计入），
   *  手工拼「+4」与 .panel-footer 的 margin+padding+border 实际结构不符会裁切底部。 */
  private viewportContentSize(): { w: number; h: number } {
    const p = this.panelEl
    const w = p?.scrollWidth ?? 0
    let h = p?.scrollHeight ?? 0
    const footer = this.panelFooterEl
    if (footer && !footer.hidden) {
      const fh = footer.offsetHeight
      if (fh > 0) {
        const mt = parseFloat(getComputedStyle(footer).marginTop) || 0
        h += fh + mt + 1 // +1 分隔线上边框（viewport 为 content-box，height 即内容高）
      }
    }
    const sub = this.subPanelEl
    if (sub && this.openSub && !sub.hidden) {
      const sw = sub.scrollWidth
      const sh = sub.scrollHeight
      return { w: sw > 0 ? sw : w, h: sh > 0 ? sh : h }
    }
    return { w, h }
  }

  private syncIndicator(): void {
    const ind = this.indicatorEl
    const barEl = this.barEl
    if (!ind || !barEl) return
    const open = this.effectiveOpen()
    if (!open) return
    const trigger = this.shadow.querySelector<HTMLElement>(`[part="top-item"][data-value="${open}"]`)
    // 触发器被收纳（从「···」弹层打开）或缺失：指示条/箭头不定位到隐藏项（offset 全 0 会错位）
    if (!trigger || trigger.hasAttribute('data-collapsed')) {
      ind.setAttribute('data-state', 'closed')
      if (this.arrowEl) this.arrowEl.hidden = true
      return
    }
    if (this.isVertical()) {
      // 坑：垂直形态开面板 toggle bar 的 vertical 类同帧触发横→竖重排，此时 offsetTop
      // 还是旧布局值（与 writeArrow 同因）——rAF 等一帧重排后写入，指示条才对准触发器
      requestAnimationFrame(() => {
        if (!this.effectiveOpen() || !this.isConnected) return
        const t = this.shadow.querySelector<HTMLElement>(`[part="top-item"][data-value="${open}"]`)
        if (!t) return
        const y = t.offsetTop
        const h = t.offsetHeight
        if (h > 0) {
          ind.style.setProperty('--ind-y', `${y}px`)
          ind.style.setProperty('--ind-h', `${h}px`)
        }
      })
    } else {
      const x = trigger.offsetLeft
      const w = trigger.offsetWidth
      if (w > 0) {
        ind.style.setProperty('--ind-x', `${x}px`)
        ind.style.setProperty('--ind-w', `${w}px`)
      }
    }
    // 箭头跟随触发器：箭头挂在 nav 直下（viewport 外），坐标系=触发器相对 nav 的 offset——
    // nav 在打开期间静止，面板 width/height 过渡不再牵动箭头；垂直/翻转形态天然正确
    // （旧实现 offsetLeft 相对 bar 但箭头在 viewport 内定位，坐标系错位致箭头指偏/越界）。
    // 垂直形态开面板会 toggle bar 的 vertical 类触发横→竖排重排，同帧 offsetTop 是旧布局值——
    // rAF 等一帧重排后写入。viewport 尺寸变量同帧重算（打开瞬间面板内容布局未稳，
    // 同步测量会比终态少几像素，营销位底缘被裁）。
    requestAnimationFrame(() => {
      if (!this.effectiveOpen() || !this.isConnected) return
      this.writeArrow(trigger)
      this.syncViewportSize()
    })
  }

  /** 箭头位置写入：触发器中心投影到 nav 坐标系（offsetLeft/offsetTop 相对 bar，
   *  补偿 bar 相对 nav 的偏移——箭头定位上下文是 nav，触发器 offsetParent 是 bar） */
  private writeArrow(trigger: HTMLElement): void {
    const ar = this.arrowEl
    const bar = this.barEl
    if (!ar || !bar) return
    const bx = bar.offsetLeft
    const by = bar.offsetTop
    if (this.isVertical()) {
      const cy = by + trigger.offsetTop + trigger.offsetHeight / 2 - 6 // 半箭头高 6px（箭头高 12，与浮层家族一致）
      ar.style.setProperty('--arrow-y', `${cy}px`)
    } else {
      const cx = bx + trigger.offsetLeft + trigger.offsetWidth / 2 - 6 // 半箭头宽 6px（箭头宽 12，与浮层家族一致）
      ar.style.setProperty('--arrow-x', `${cx}px`)
    }
  }

  private syncActive(): void {
    if (!this.shadow) return
    for (const el of this.shadow.querySelectorAll('.active')) el.classList.remove('active')
    const item = this.itemsList[this.activeIndex]
    if (!item || item.value == null) return
    this.shadow.querySelector<HTMLElement>(`[part="top-item"][data-value="${item.value}"]`)?.classList.add('active')
  }

  private syncRoving(): void {
    if (!this.shadow) return
    const open = this.effectiveOpen()
    const target = open || this.itemsList[this.activeIndex]?.value || ''
    for (const el of this.shadow.querySelectorAll<HTMLElement>('[part="top-item"]')) {
      const v = el.dataset.value ?? ''
      // 被收纳的隐藏项恒 -1（不可聚焦），其余仅 roving 基准项进 Tab 顺序
      el.setAttribute('tabindex', v === target && !this.collapsedValues.has(v) ? '0' : '-1')
    }
  }

  private focusCurrent(): void {
    if (!this.keyboardMode) return
    const open = this.effectiveOpen()
    if (open && this.shadow.activeElement && this.panelEl?.contains(this.shadow.activeElement as Node)) {
      return
    }
    if (!open) {
      const item = this.itemsList[this.activeIndex]
      if (item?.value == null) return
      this.shadow.querySelector<HTMLElement>(`[part="top-item"][data-value="${item.value}"]`)?.focus()
    }
  }

  // ================= 键盘导航 =================

  private handleKey(e: KeyboardEvent): void {
    const open = this.effectiveOpen()
    const active = this.shadow.activeElement as Node | null
    const inPanel =
      !!open &&
      ((active != null && this.panelEl?.contains(active)) || (active != null && this.subPanelEl?.contains(active)))
    if (inPanel) {
      this.handlePanelKey(e)
      return
    }
    this.handleTopKey(e)
  }

  private handleTopKey(e: KeyboardEvent): void {
    // 「···」收纳弹层打开时 Esc 收起并归还焦点（不进入下方的主面板/顶级逻辑）
    if (this.moreOpen) {
      if (e.key === 'Escape') {
        e.preventDefault()
        this.closeOverflow(true)
      }
      return
    }
    const items = this.itemsList
    // 被收纳的隐藏顶级项不参与键盘导航（焦点落不到 display:none 上）
    const enabled = items
      .map((i, idx) => (i.disabled || this.collapsedValues.has(i.value ?? '') ? -1 : idx))
      .filter((i) => i >= 0)
    if (enabled.length === 0) return
    this.keyboardMode = true
    const vertical = this.isVertical()
    const rtl = isRtl(this)
    // 横向形态方向键跟随书写方向：RTL 下 ArrowRight = 向书写起点回退（prev）
    const prevKey = vertical ? 'ArrowUp' : rtl ? 'ArrowRight' : 'ArrowLeft'
    const nextKey = vertical ? 'ArrowDown' : rtl ? 'ArrowLeft' : 'ArrowRight'
    const openKey = vertical ? 'ArrowRight' : 'ArrowDown'
    const active = items[this.activeIndex]
    if (e.key === prevKey) {
      e.preventDefault()
      this.moveActive(enabled, -1)
    } else if (e.key === nextKey) {
      e.preventDefault()
      this.moveActive(enabled, 1)
    } else if (e.key === openKey) {
      e.preventDefault()
      if (active && !active.disabled && active.children?.length) {
        this.open(active.value ?? '')
        this.panelIndex = this.firstEnabledPanelIndex()
        this.syncRoving()
        this.focusPanel()
      }
    } else if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault()
      if (!active || active.disabled) return
      if (active.children?.length) {
        if (this.effectiveOpen() === active.value) {
          this.close()
          this.syncRoving()
          this.focusCurrent()
        } else {
          this.open(active.value ?? '')
          this.panelIndex = this.firstEnabledPanelIndex()
          this.syncRoving()
          this.focusPanel()
        }
      } else {
        this.select(active)
      }
    } else if (e.key === 'Escape') {
      if (this.effectiveOpen()) {
        e.preventDefault()
        this.close()
        this.syncRoving()
        this.focusCurrent()
      }
    } else if (e.key === 'Home') {
      e.preventDefault()
      this.activeIndex = enabled[0]!
      this.syncActive()
      this.syncRoving()
      this.focusCurrent()
    } else if (e.key === 'End') {
      e.preventDefault()
      this.activeIndex = enabled[enabled.length - 1]!
      this.syncActive()
      this.syncRoving()
      this.focusCurrent()
    } else {
      return
    }
    this.syncActive()
    this.syncRoving()
    if (!(e.key === 'Enter' && active?.children?.length && this.effectiveOpen() === active.value)) {
      this.focusCurrent()
    }
  }

  private handlePanelKey(e: KeyboardEvent): void {
    // 二级覆盖面板打开：键盘在其内部独立处理（Esc/ArrowLeft 逐层回退，不动主面板）
    if (this.openSub) {
      this.handleSubKey(e)
      return
    }
    const focusables = this.panelFocusables()
    const enabled = focusables
      .map((el, i) => (el.getAttribute('aria-disabled') === 'true' ? -1 : i))
      .filter((i) => i >= 0)
    if (enabled.length === 0) return
    this.keyboardMode = true
    // 面板内横向键跟随书写方向（grid 列序 RTL 下视觉反转）：进/出 section 与二级面板互换
    const rtl = isRtl(this)
    const fwdKey = rtl ? 'ArrowLeft' : 'ArrowRight'
    const backKey = rtl ? 'ArrowRight' : 'ArrowLeft'
    const cur = this.shadow.activeElement as HTMLElement | null
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault()
      this.movePanel(enabled, e.key === 'ArrowDown' ? 1 : -1)
    } else if (e.key === fwdKey) {
      if (cur?.getAttribute('part') === 'section-title') {
        e.preventDefault()
        const val = cur.dataset.value ?? ''
        if (this.openSection !== val) this.toggleSection(val)
        const sec = cur.closest('[part="section"]')
        const first = sec?.querySelector<HTMLElement>('[part="section-links"] a')
        ;(first as HTMLElement | null)?.focus()
      } else if (cur?.getAttribute('part') === 'sub-trigger') {
        // 二级触发器 → 展开二级面板并聚焦首项（级联进入）
        e.preventDefault()
        this.openSubPanel(cur.dataset.value ?? '')
      }
    } else if (e.key === backKey) {
      const inSection = cur?.closest('[part="section-links"]')
      if (inSection) {
        e.preventDefault()
        const title = inSection.closest('[part="section"]')?.querySelector<HTMLElement>('[part="section-title"]')
        ;(title as HTMLElement | null)?.focus()
      } else {
        // 面板第一项 ← 收起并回顶级
        e.preventDefault()
        this.close()
        this.syncRoving()
        this.focusCurrent()
      }
    } else if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault()
      if (!cur) return
      if (cur.getAttribute('part') === 'section-title') {
        const val = cur.dataset.value ?? ''
        this.toggleSection(val)
      } else {
        cur.click()
      }
    } else if (e.key === 'Escape') {
      e.preventDefault()
      this.close()
      this.syncRoving()
      this.focusCurrent()
    } else if (e.key === 'Home') {
      e.preventDefault()
      this.panelIndex = enabled[0]!
      this.focusPanel()
    } else if (e.key === 'End') {
      e.preventDefault()
      this.panelIndex = enabled[enabled.length - 1]!
      this.focusPanel()
    } else {
      return
    }
    this.syncActive()
  }

  /** 二级覆盖面板内键盘：上下移动、Enter 选择、Esc/ArrowLeft 回退到主面板（焦点回触发器） */
  private handleSubKey(e: KeyboardEvent): void {
    const list = this.subFocusables()
    const enabled = list.map((el, i) => (el.getAttribute('aria-disabled') === 'true' ? -1 : i)).filter((i) => i >= 0)
    if (enabled.length === 0) return
    this.keyboardMode = true
    // RTL：回退方向键镜像（ArrowRight 回退）
    const backKey = isRtl(this) ? 'ArrowRight' : 'ArrowLeft'
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault()
      const cur = enabled.indexOf(this.panelIndex)
      const next = (cur + (e.key === 'ArrowDown' ? 1 : -1) + enabled.length) % enabled.length
      this.panelIndex = enabled[next]!
      this.focusSubPanel()
    } else if (e.key === backKey || e.key === 'Escape') {
      e.preventDefault()
      this.closeSubPanel()
    } else if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault()
      const cur = this.shadow.activeElement as HTMLElement | null
      cur?.click()
    } else if (e.key === 'Home') {
      e.preventDefault()
      this.panelIndex = enabled[0]!
      this.focusSubPanel()
    } else if (e.key === 'End') {
      e.preventDefault()
      this.panelIndex = enabled[enabled.length - 1]!
      this.focusSubPanel()
    }
  }

  private panelFocusables(): HTMLElement[] {
    // 二级覆盖面板打开时，焦点集合 = 二级面板项（主面板不可达）
    if (this.openSub) return this.subFocusables()
    const p = this.panelEl
    if (!p) return []
    return [
      ...p.querySelectorAll<HTMLElement>(
        '[part="card-link"], [part="section-title"], [part="sub-trigger"], [part="section-links"] a',
      ),
    ]
  }

  private firstEnabledPanelIndex(): number {
    const list = this.panelFocusables()
    return list.findIndex((el) => el.getAttribute('aria-disabled') !== 'true')
  }

  private moveActive(enabled: number[], dir: 1 | -1): void {
    const len = enabled.length
    if (len === 0) return
    const cur = enabled.indexOf(this.activeIndex)
    if (!this.loopEnabled()) {
      const next = cur + dir
      if (next >= 0 && next < len) this.activeIndex = enabled[next]!
      return
    }
    this.activeIndex = enabled[(cur + dir + len) % len]!
  }

  private movePanel(enabled: number[], dir: 1 | -1): void {
    const len = enabled.length
    if (len === 0) return
    let cur = enabled.indexOf(this.panelIndex)
    if (cur < 0) cur = dir === 1 ? -1 : len
    this.panelIndex = enabled[(cur + dir + len) % len]!
    this.focusPanel()
  }

  private focusPanel(): void {
    const list = this.panelFocusables()
    const el = list[this.panelIndex]
    if (el) el.focus()
  }

  /** 文档级键盘：面板打开时 Tab 焦点陷阱 */
  private handleDocumentKey = (e: KeyboardEvent): void => {
    if (e.key !== 'Tab' || !this.effectiveOpen()) return
    const focusables = this.panelFocusables()
    if (focusables.length === 0) return
    const active = this.shadow.activeElement as Node | null
    const inPanel =
      (active != null && this.panelEl?.contains(active)) || (active != null && this.subPanelEl?.contains(active))
    if (!inPanel) return
    e.preventDefault()
    const current = focusables.indexOf(this.shadow.activeElement as HTMLElement)
    const next = e.shiftKey ? (current - 1 + focusables.length) % focusables.length : (current + 1) % focusables.length
    focusables[next]?.focus()
  }
}
